import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Edit2,
  Package,
  MoreHorizontal,
  Layers,
  Trash2,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TablePagination } from '@/components/TablePagination';

import { API_URL } from '@/lib/api';

const Products = () => {
  const navigate = useNavigate();
  const [products,      setProducts]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [categoryFilter,setCategoryFilter]= useState('all');
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [isDeleting,    setIsDeleting]    = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [search, categoryFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const mapProduct = (p) => ({
    id: p.id,
    name: p.name,
    product_code: p.productCode,
    category_id: p.categoryId,
    category_name: p.categoryName,
    image_url: p.image,
    description: p.description || '',
    total_stock: p.totalStock ?? 0,
    variant_count: p.variantCount ?? 0,
    status: p.status === true ? 'active' : 'inactive'
  });

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') params.append('categoryId', categoryFilter);
      const response = await axios.get(`${API_URL}/products?${params}`);
      let list = (response.data || []).map(mapProduct);
      if (search) {
        const q = search.toLowerCase();
        list = list.filter(p => `${p.name} ${p.product_code}`.toLowerCase().includes(q));
      }
      setProducts(list);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      await axios.put(`${API_URL}/products/${product.id}`, { status: product.status !== 'active' });
      toast.success('Product status updated');
      fetchProducts();
    } catch {
      toast.error('Failed to update product status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/products/${deleteTarget.id}`);
      toast.success('Product deleted');
      setDeleteTarget(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div data-testid="products-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="text-slate-500 mt-1">Manage your product catalog</p>
        </div>
        <Button
          onClick={() => navigate('/products/new')}
          className="rounded-xl gap-2"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-56" data-testid="category-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-12 h-12 rounded-xl shimmer flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 rounded-lg shimmer w-48" />
                  <div className="h-3 rounded-lg shimmer w-28" />
                </div>
                <div className="h-3.5 rounded-lg shimmer w-20" />
                <div className="h-3.5 rounded-lg shimmer w-20" />
                <div className="h-6 rounded-full shimmer w-16" />
                <div className="h-8 rounded-xl shimmer w-8" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-base font-semibold text-slate-700">No products found</p>
            <p className="text-sm text-slate-400 mt-1">
              {search ? `No results for "${search}"` : 'Create your first product to get started'}
            </p>
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Product</TableHead>
                <TableHead>Product Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Variants</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.slice((page - 1) * pageSize, page * pageSize).map((product) => (
                <TableRow key={product.id} className="table-row-hover" data-testid={`product-row-${product.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-purple-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{product.name}</p>
                        {product.description && (
                          <p className="text-sm text-slate-500 truncate max-w-xs">{product.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{product.product_code}</TableCell>
                  <TableCell>{product.category_name}</TableCell>
                  <TableCell>{product.variant_count}</TableCell>
                  <TableCell className={product.total_stock === 0 ? 'text-rose-600 font-medium' : ''}>
                    {product.total_stock}
                  </TableCell>
                  <TableCell>
                    <Badge className={`status-${product.status}`}>
                      {product.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`product-actions-${product.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/products/${product.id}/edit`)}>
                          <Edit2 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/products/${product.id}/variants`)}>
                          <Layers className="w-4 h-4 mr-2" /> Manage Variants
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleStatus(product)}>
                          {product.status === 'active' ? 'Disable' : 'Enable'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(product)}
                          className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            data={products}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          />
          </>
        )}
      </div>
      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Delete Product
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            </p>
            <p className="text-xs text-slate-500 mt-1">This will also delete all variants and remove it from carts. This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700 text-white">
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
